import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import { makeStyles } from 'tss-react/mui';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Link from 'next/link';
import React from 'react';
import imageCrop from 'helpers/imageCrop';
import { getLocationDetails } from '../../helpers/getLocationDetails';
import shadow from '../../helpers/shadow';
import Excerpt from '../Grid/Excerpt';
import Image from 'components/Image';
import VideoIcon from 'components/VideoIcon';

const useStyles = makeStyles()(() => ({
  card: {
    width: '100%',
    minWidth: '250px',
    maxWidth: '400px',
    borderRadius: 12,
    boxShadow: shadow.md,
  },
  loctitle: {
    lineHeight: '3em',
    maxHeight: '3em',
    overflow: 'hidden',
    position: 'relative',
  },
  imgBox: {
    minHeight: '200px',
  },
  textBox: {
    height: '175px',
  },
}));

const SimilarPostCard = props => {
  const { classes } = useStyles();

  const isSkeleton = !props.post.author;

  const mediaPlaceholder = (
    <Skeleton variant="rectangular" width="100%" height={200} />
  );

  const { locationTitle, locationSubtitle, locationLink } = getLocationDetails(
    props.post.location,
  );

  const card = (
    <Card className={classes.card}>
      <CardActionArea
        aria-label={(props && props.post && props.post.title) || 'Loader'}
      >
        {isSkeleton
          ? mediaPlaceholder
          : props.post.thumb &&
            props.post.thumb.url && (
              <div className="tw-w-full tw-overflow-hidden tw-relative tw-h-48">
                <Image
                  unoptimized
                  className="tw-object-cover tw-object-center"
                  src={imageCrop(props.post.thumb.url, 612, 420)}
                  layout="fill"
                  alt={props.post.title}
                  blurDataURL={props.post.thumb.blur}
                  placeholder={props.post.thumb.blur ? 'blur' : undefined}
                />
                {props.post.isVideo && <VideoIcon size={24} />}
              </div>
            )}
        <CardContent>
          <div className={`${classes.textBox} container`}>
            <div className="row h-100">
              <div className="my-auto col-12">
                {isSkeleton ? (
                  <>
                    <Skeleton
                      variant="text"
                      width="30%"
                      height={18}
                      className="mx-auto"
                    />
                    <Skeleton
                      variant="text"
                      width="80%"
                      height={30}
                      className="mx-auto"
                    />
                    <Skeleton
                      variant="text"
                      width="70%"
                      height={30}
                      className="mx-auto"
                    />
                    <Skeleton
                      variant="text"
                      width="40%"
                      height={18}
                      className="mx-auto"
                    />
                  </>
                ) : (
                  <>
                    {props.post.location && (
                      <div className={`${classes.loctitle}`}>
                        <Link
                          href={locationLink}
                          color="primary"
                          legacyBehavior
                        >
                          <a className="hoverline">
                            <Tooltip
                              title={locationSubtitle}
                              placement="bottom"
                            >
                              <Typography variant="overline">
                                {locationTitle}
                              </Typography>
                            </Tooltip>
                          </a>
                        </Link>
                      </div>
                    )}
                    <Excerpt title={props.post.title} />

                    <Typography variant="subtitle2" color="textSecondary">
                      <Link
                        color="textSecondary"
                        href={`/@${props.post.author}`}
                        legacyBehavior
                      >
                        <a className="hoverline">by @{props.post.author}</a>
                      </Link>
                    </Typography>
                  </>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </CardActionArea>
    </Card>
  );

  if (isSkeleton) return card;

  return (
    <>
      <Link
        color="textPrimary"
        href={`/@${props.post.author}/${props.post.permlink}`}
        className="w-100 d-flex justify-content-center"
      >
        {card}
      </Link>
    </>
  );
};

export default SimilarPostCard;
